import {Component, Inject} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {CheckoutRewardsSelection} from '../checkout-rewards/checkout-rewards.component';
import {Discount} from '../../../_models/discount';

export interface CheckoutRewardsDialogData {
    /** Order total in cents — used for manual promo discount preview */
    orderTotal: number;
    /** Line items for FREE_ITEM reward detection */
    lineItems?: any[];
    /** When false, auto-rewards section is hidden */
    rewardsEnabled?: boolean;
}

@Component({
    selector: 'app-checkout-rewards-dialog',
    template: `
        <div class="dialog-wrap">
            <div class="dialog-header">
                <mat-icon>loyalty</mat-icon>
                <span>Apply Discount or Promo</span>
                <button class="close-btn" (click)="close(null)"><mat-icon>close</mat-icon></button>
            </div>

            <!-- Tab bar -->
            <div class="tab-bar">
                <button class="tab-btn" [class.active]="activeTab === 'discount'" (click)="activeTab = 'discount'">
                    <mat-icon>discount</mat-icon> Custom Discount
                </button>
                <button class="tab-btn" [class.active]="activeTab === 'promo'" (click)="activeTab = 'promo'">
                    <mat-icon>local_offer</mat-icon> Rewards & Promo
                </button>
            </div>

            <div class="dialog-body">
                <!-- Custom Discount Tab -->
                <div *ngIf="activeTab === 'discount'" class="discount-tab">
                    <div class="discount-error" *ngIf="discountError">{{ discountError }}</div>
                    <div class="discount-input-wrap">
                        <input *ngIf="discountType === 'amount'" type="text" class="discount-input"
                               placeholder="Enter Amount" [ngModel]="discountValue"
                               (ngModelChange)="onAmountChange($event)">
                        <span *ngIf="discountType === 'percent'" class="percent-wrap">
                            <input type="number" class="discount-input" placeholder="Enter Percent"
                                   [(ngModel)]="discountValue">%
                        </span>
                    </div>
                    <div class="type-toggle">
                        <button class="toggle-btn" [class.active]="discountType === 'amount'"
                                (click)="discountType = 'amount'; discountValue = ''; discountError = ''">Amount</button>
                        <button class="toggle-btn" [class.active]="discountType === 'percent'"
                                (click)="discountType = 'percent'; discountValue = ''; discountError = ''">Percentage</button>
                    </div>
                </div>

                <!-- Rewards & Promo Tab -->
                <div *ngIf="activeTab === 'promo'">
                    <app-checkout-rewards
                        [orderTotal]="data.orderTotal"
                        [lineItems]="data.lineItems || []"
                        [rewardsEnabled]="data.rewardsEnabled !== false"
                        (selectionChange)="onChange($event)">
                    </app-checkout-rewards>
                </div>
            </div>

            <div class="dialog-actions">
                <button class="cancel-btn" (click)="close(null)">Cancel</button>
                <button class="apply-btn" [disabled]="!canApply()" (click)="apply()">
                    Apply
                </button>
            </div>
        </div>
    `,
    styles: [`
        .dialog-wrap { display: flex; flex-direction: column; min-width: 360px; max-width: 480px; }
        .dialog-header { display: flex; align-items: center; gap: 8px; padding: 12px 16px; border-bottom: 1px solid #eee; font-weight: 600; }
        .dialog-header .close-btn { margin-left: auto; background: none; border: none; cursor: pointer; }
        .tab-bar { display: flex; border-bottom: 2px solid #eee; }
        .tab-btn {
            flex: 1; display: flex; align-items: center; justify-content: center; gap: 6px;
            padding: 10px 12px; border: none; background: #f5f5f5; cursor: pointer;
            font-size: 14px; font-weight: 500; color: #666; transition: all 0.2s;
        }
        .tab-btn.active { background: #fff; color: #1976d2; border-bottom: 2px solid #1976d2; margin-bottom: -2px; }
        .tab-btn:hover:not(.active) { background: #ebebeb; }
        .dialog-body { padding: 16px; min-height: 120px; }
        .discount-tab { display: flex; flex-direction: column; gap: 12px; }
        .discount-error { color: #d32f2f; font-size: 13px; }
        .discount-input-wrap { display: flex; align-items: center; }
        .discount-input {
            width: 100%; padding: 10px 12px; border: 1px solid #ccc; border-radius: 6px;
            font-size: 16px; outline: none;
        }
        .discount-input:focus { border-color: #1976d2; }
        .percent-wrap { display: flex; align-items: center; width: 100%; font-size: 18px; font-weight: 600; }
        .percent-wrap .discount-input { margin-right: 4px; }
        .type-toggle { display: flex; gap: 0; border-radius: 6px; overflow: hidden; border: 1px solid #ccc; }
        .toggle-btn {
            flex: 1; padding: 8px 12px; border: none; background: #f5f5f5;
            cursor: pointer; font-size: 14px; transition: all 0.2s;
        }
        .toggle-btn.active { background: #1976d2; color: #fff; }
        .dialog-actions { display: flex; justify-content: flex-end; gap: 8px; padding: 12px 16px; border-top: 1px solid #eee; }
        .cancel-btn { padding: 8px 16px; border-radius: 6px; border: 1px solid #ccc; background: #fff; cursor: pointer; }
        .apply-btn { padding: 8px 16px; border-radius: 6px; border: none; background: #1976d2; color: #fff; cursor: pointer; }
        .apply-btn:disabled { background: #9e9e9e; cursor: not-allowed; }
    `]
})
export class CheckoutRewardsDialogComponent {
    selection: CheckoutRewardsSelection | null = null;
    activeTab: 'discount' | 'promo' = 'discount';

    // Custom discount fields
    discountType: 'amount' | 'percent' = 'amount';
    discountValue = '';
    discountError = '';

    constructor(
        private dialogRef: MatDialogRef<CheckoutRewardsDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: CheckoutRewardsDialogData
    ) {}

    onChange(sel: CheckoutRewardsSelection) {
        this.selection = sel;
    }

    onAmountChange(event: string) {
        const value = event ? parseFloat(event.replace('.', '')).toString() : '';
        if (value) {
            if (value.length > 2) {
                this.discountValue = value.slice(0, -2) + '.' + value.slice(-2).padStart(2, '0');
            } else {
                this.discountValue = value !== '0' ? '.' + value.slice(-2).padStart(2, '0') : '';
            }
        } else {
            this.discountValue = '';
        }
    }

    canApply(): boolean {
        if (this.activeTab === 'discount') {
            return Number(this.discountValue) > 0;
        }
        if (!this.selection) return false;
        return !!(this.selection.discount || this.selection.customerNumber);
    }

    apply() {
        if (this.activeTab === 'discount') {
            const val = Number(this.discountValue);
            if (val <= 0) {
                this.discountError = 'Please enter a valid ' + this.discountType;
                return;
            }
            const discount = new Discount();
            discount.name = 'Employee Added Discount';
            if (this.discountType === 'amount') {
                discount.amount = Number(this.discountValue.replace('.', '')) * -1;
            } else {
                discount.percentage = val;
            }
            // Return as a CheckoutRewardsSelection with only discount set
            this.close({
                customerNumber: null,
                discount,
                manualPromo: null,
                autoReward: null,
                recordVisit: false,
            });
        } else {
            this.close(this.selection);
        }
    }

    close(result: CheckoutRewardsSelection | null) {
        this.dialogRef.close(result);
    }
}
